import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import DogImage from "@/components/DogImage";
import { useAuth } from "@/context/AuthContext";
import { Heart, X, Filter, Check, MinusCircle, PawPrint, MessageCircle, ShoppingBag } from "lucide-react";
import PageMeta from "@/components/PageMeta";
import { dogShopUrl } from "@/lib/dogShop";
import { SHOPIFY_URL } from "@/constants/config";

export default function Adopt() {
  const [dogs, setDogs] = useState([]);
  const [filter, setFilter] = useState("available");
  const [selected, setSelected] = useState(null);
  const { user, isFavorite, toggleFavorite } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dogs").then((r) => setDogs(r.data));
  }, []);

  useEffect(() => {
    if (location.hash && dogs.length) {
      const id = location.hash.slice(1);
      const d = dogs.find((x) => x.id === id);
      if (d) setSelected(d);
    }
  }, [location, dogs]);

  const filtered = useMemo(() => {
    if (filter === "all") return dogs;
    if (filter === "urgent") return dogs.filter((d) => d.featured);
    return dogs.filter((d) => d.status === filter);
  }, [dogs, filter]);

  return (
    <div>
      <PageMeta
        title="Adopt a Bulldog — Available Dogs | Lavish 808 Bulliez Rescued Paws"
        description="Meet bulldogs, French bulldogs and bully breeds available for adoption in Murrieta, Riverside County and across Southern California. Every rescue is vetted, loved, and ready for their forever ʻohana."
      />
      <section className="pt-20 pb-12" style={{ backgroundColor: "#F7ECD9" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "#E27E4E" }}>
            Adopt • Foster • Love
          </div>
          <h1 className="font-serif text-5xl md:text-7xl mb-6" style={{ color: "#0E5C6E" }}>
            Find your ʻohana.
          </h1>
          <p className="text-lg max-w-2xl leading-relaxed" style={{ color: "#2D2B2A" }}>
            Each of these bulldogs came to us with a story. Every one is ready for the next
            chapter — with you. Mālama a life. Change your own.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center gap-3 mb-10">
            <Filter size={16} style={{ color: "#0E5C6E" }} />
            {["all", "urgent", "available", "pending", "adopted"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className="px-4 py-2 rounded-full text-sm capitalize transition-colors"
                style={{
                  backgroundColor: filter === f ? "#0E5C6E" : "transparent",
                  color: filter === f ? "white" : "#0E5C6E",
                  border: `1px solid ${filter === f ? "#0E5C6E" : "#EADCC5"}`,
                }}
                data-testid={`filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20" style={{ color: "#6B655E" }}>
              <PawPrint size={40} className="mx-auto mb-4 opacity-50" />
              No dogs match this filter yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((d) => (
                <div key={d.id} className="card-dog relative" data-testid={`dog-card-${d.name.toLowerCase()}`}>
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFavorite(d.id); }}
                    className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full flex items-center justify-center bg-white/95 shadow transition-transform hover:scale-110"
                    aria-label={isFavorite(d.id) ? "Remove favorite" : "Add favorite"}
                    data-testid={`fav-toggle-${d.name.toLowerCase()}`}
                  >
                    <Heart size={18} fill={isFavorite(d.id) ? "#E27E4E" : "none"} color={isFavorite(d.id) ? "#E27E4E" : "#0E5C6E"} />
                  </button>
                  <button
                    onClick={() => { setSelected(d); navigate(`/adopt#${d.id}`, { replace: true }); }}
                    className="text-left w-full"
                  >
                    <div className="aspect-[4/5] overflow-hidden">
                      <DogImage dog={d} />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-serif text-2xl" style={{ color: "#0E5C6E" }}>{d.name}</h3>
                        <span className={`badge badge-${d.featured ? "urgent" : d.status}`}>
                          {d.featured ? "Urgent" : d.status}
                        </span>
                      </div>
                      <div className="text-sm mb-4" style={{ color: "#6B655E" }}>
                        {d.breed} • {d.age} • {d.sex}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {d.personality.slice(0, 3).map((p) => (
                          <span key={p} className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: "#F7ECD9", color: "#0E5C6E" }}>
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {selected && <DogModal dog={selected} onClose={() => { setSelected(null); navigate("/adopt", { replace: true }); }} />}
    </div>
  );
}

function DogModal({ dog, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    dog_id: dog.id, dog_name: dog.name,
    full_name: user?.name || "", email: user?.email || "", phone: "", address: "",
    housing_type: "house", has_yard: false, other_pets: "", experience: "", reason: "",
  });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/adoption-inquiries", form);
      setSent(true);
    } catch (err) {
      alert("Something went wrong. Please try again or email us directly.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="dog-modal">
      <div className="bg-white rounded-3xl max-w-5xl w-full my-8 overflow-hidden grid grid-cols-1 md:grid-cols-2" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          {dog.video_url ? (
            <video src={dog.video_url} autoPlay loop muted playsInline poster={dog.image_url} className="w-full h-full object-cover md:aspect-auto aspect-square" />
          ) : (
            <div className="w-full h-full md:aspect-auto aspect-square overflow-hidden">
              <DogImage dog={dog} />
            </div>
          )}
          <button onClick={onClose} className="absolute top-4 right-4 md:hidden bg-white rounded-full p-2" data-testid="modal-close-mobile"><X size={18} /></button>
        </div>
        <div className="p-8 md:p-10 max-h-[80vh] overflow-y-auto relative">
          <button onClick={onClose} className="absolute top-4 right-4 hidden md:block" data-testid="modal-close"><X size={20} /></button>
          <span className={`badge badge-${dog.featured ? "urgent" : dog.status} mb-4`}>
            {dog.featured ? "Urgent" : dog.status}
          </span>
          <h2 className="font-serif text-4xl mb-2" style={{ color: "#0E5C6E" }}>{dog.name}</h2>
          <div className="text-sm mb-6" style={{ color: "#6B655E" }}>{dog.breed} • {dog.age} • {dog.sex} • {dog.size} • {dog.location}</div>
          <p className="leading-relaxed mb-6" style={{ color: "#2D2B2A" }}>{dog.story}</p>

          <div className="grid grid-cols-2 gap-3 mb-6 text-sm">
            <TraitLine ok={dog.good_with_kids} label="Good with kids" />
            <TraitLine ok={dog.good_with_dogs} label="Good with dogs" />
            <TraitLine ok={dog.house_trained} label="House trained" />
            {dog.special_needs && <div className="col-span-2 p-3 rounded-xl text-sm" style={{ backgroundColor: "#FDECE7", color: "#8A3A28" }}>💗 {dog.special_needs}</div>}
          </div>

          {dog.zeffy_url && (
            <a href={dog.zeffy_url} target="_blank" rel="noopener noreferrer" className="btn-donate w-full justify-center mb-3" data-testid={`dog-donate-${dog.name.toLowerCase()}`}>
              <Heart size={16} fill="white" /> Support {dog.name}'s Care
            </a>
          )}

          <a
            href={dog.shop_ready ? dogShopUrl(dog.name) : SHOPIFY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 justify-center w-full px-5 py-3 rounded-full text-sm font-medium border transition-colors mb-3"
            style={{ borderColor: "#E27E4E", color: "#E27E4E", backgroundColor: "white" }}
            data-testid={`dog-shop-${dog.name.toLowerCase().replace(/[^a-z]/g, "")}`}
          >
            <ShoppingBag size={16} />
            {dog.shop_ready
              ? `Shop ${dog.name.split(/[(\-]/)[0].trim()}'s Collection`
              : `Team ${dog.name.split(/[(\-]/)[0].trim()} Merch — Coming Soon`}
          </a>

          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("aloha-chat", { detail: { message: `Tell me more about ${dog.name} — I'm considering adopting.` } }))}
            className="btn-outline w-full justify-center mb-6"
            data-testid={`dog-chat-${dog.name.toLowerCase()}`}
          >
            <MessageCircle size={16} /> Chat about {dog.name}
          </button>

          {sent ? (
            <div className="p-6 rounded-2xl text-center" style={{ backgroundColor: "#E4EFE9", color: "#0A4655" }} data-testid="inquiry-success">
              <Heart size={24} className="mx-auto mb-2" fill="#0E5C6E" color="#0E5C6E" />
              <div className="font-serif text-2xl mb-1">Mahalo!</div>
              <div className="text-sm">We received your inquiry about {dog.name}. Our team will reach out within 48 hours.</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="adoption-inquiry-form">
              <div className="font-serif text-xl mb-2" style={{ color: "#0E5C6E" }}>Meet {dog.name}</div>
              <input required placeholder="Full name" className="form-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="inquiry-name" />
              <div className="grid grid-cols-2 gap-3">
                <input required type="email" placeholder="Email" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inquiry-email" />
                <input required placeholder="Phone" className="form-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inquiry-phone" />
              </div>
              <input required placeholder="Home address (city, state)" className="form-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="inquiry-address" />
              <div className="grid grid-cols-2 gap-3">
                <select className="form-input" value={form.housing_type} onChange={(e) => setForm({ ...form, housing_type: e.target.value })} data-testid="inquiry-housing">
                  <option value="house">House</option>
                  <option value="apartment">Apartment</option>
                  <option value="condo">Condo</option>
                </select>
                <label className="form-input flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.has_yard} onChange={(e) => setForm({ ...form, has_yard: e.target.checked })} data-testid="inquiry-yard" /> Fenced yard
                </label>
              </div>
              <textarea rows="3" placeholder={`Why do you want to adopt ${dog.name}?`} className="form-input" required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="inquiry-reason" />
              <button type="submit" className="btn-primary w-full justify-center" data-testid="inquiry-submit">
                Submit inquiry
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function TraitLine({ ok, label }) {
  return (
    <div className="flex items-center gap-2">
      {ok ? <Check size={16} color="#0E5C6E" /> : <MinusCircle size={16} color="#E27E4E" />}
      <span>{label}</span>
    </div>
  );
}
